export type NationalProjectEvidenceKind = 'source' | 'inference' | 'mixed' | 'unknown';

export type NationalProjectCouncil = {
  name: string;
  slug: string | null;
  application_count: number;
};

export type NationalProjectApplication = {
  application_uid: string;
  reference_no: string;
  title: string;
  council: string;
  address: string;
  status: string;
  source_url: string | null;
  relationship: NationalProjectEvidenceKind;
  source_hash: string | null;
};

export type NationalProjectSource = {
  source_key: string;
  label: string;
  publisher: string;
  url: string | null;
  checked_at: string | null;
  evidence_kind: NationalProjectEvidenceKind;
};

export type NationalProjectTimelineEvent = {
  event_key: string;
  at: string;
  title: string;
  detail: string;
  evidence_kind: NationalProjectEvidenceKind;
  source_url: string | null;
};

export type PublicNationalProject = {
  project_key: string;
  slug: string | null;
  title: string;
  summary: string;
  status: string;
  source_state: string;
  review_state: string;
  priority: string;
  category: string;
  confidence: number | null;
  evidence_kind: NationalProjectEvidenceKind;
  evidence_summary: string;
  evidence_labels: {
    summary: string;
    claims: string;
    applications: string;
  };
  councils: NationalProjectCouncil[];
  council_count: number;
  application_count: number;
  evidence_count: number;
  inference_count: number;
  first_seen_at: string | null;
  updated_at: string | null;
  tags: string[];
  applications: NationalProjectApplication[];
  sources: NationalProjectSource[];
  timeline: NationalProjectTimelineEvent[];
};

export type NationalProjectsCoverage = {
  project_count: number;
  linked_application_count: number;
  source_backed_link_count: number;
  inferred_link_count: number;
  councils_represented: number;
  councils_expected: number;
  projects_with_complete_evidence: number;
};

export type NationalProjectsSnapshot = {
  available: boolean;
  generated_at: string | null;
  unavailable_reason: string | null;
  coverage: NationalProjectsCoverage;
  projects: PublicNationalProject[];
};

type UnknownRecord = Record<string, unknown>;

export class NationalProjectsApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status = 0, body: unknown = null) {
    super(message);
    this.name = 'NationalProjectsApiError';
    this.status = status;
    this.body = body;
  }
}

const isRecord = (value: unknown): value is UnknownRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const record = (value: unknown): UnknownRecord => (isRecord(value) ? value : {});
const array = (value: unknown): unknown[] => Array.isArray(value) ? value : [];
const text = (value: unknown, fallback = '') => typeof value === 'string' ? value : fallback;
const nullableText = (value: unknown) => typeof value === 'string' && value.trim() ? value : null;
const safeUrl = (value: unknown) => {
  const candidate = nullableText(value);
  if (!candidate) return null;
  if (candidate.startsWith('/') && !candidate.startsWith('//')) return candidate;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? candidate : null;
  } catch {
    return null;
  }
};
const finite = (value: unknown, fallback = 0) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const nullableFinite = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const stringArray = (value: unknown) => array(value).filter((item): item is string => typeof item === 'string');

const evidenceKind = (value: unknown): NationalProjectEvidenceKind => {
  if (value === 'source' || value === 'source-backed' || value === 'evidence') return 'source';
  if (value === 'inference' || value === 'inferred') return 'inference';
  if (value === 'mixed') return 'mixed';
  return 'unknown';
};

const priorityLabel = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return `${Math.round(value)}/100`;
  return text(value, 'Normal');
};

function publicApiOrigin(): string {
  const configured =
    process.env.NEXT_PUBLIC_CITYSCOPE_API_ORIGIN?.trim() ||
    process.env.NEXT_PUBLIC_API_BASE_URL?.trim() ||
    process.env.NEXT_PUBLIC_API_URL?.trim();
  if (!configured || configured.startsWith('/')) return 'https://api.city-scope.co.uk';
  try { return new URL(configured).origin; } catch { return 'https://api.city-scope.co.uk'; }
}

export function getNationalProjectsUrl(): string {
  return `${publicApiOrigin()}/api/v1/core/national-projects`;
}

export function createUnavailableNationalProjectsSnapshot(
  reason = 'The national projects register has not returned live data.',
): NationalProjectsSnapshot {
  return {
    available: false,
    generated_at: null,
    unavailable_reason: reason,
    coverage: {
      project_count: 0,
      linked_application_count: 0,
      source_backed_link_count: 0,
      inferred_link_count: 0,
      councils_represented: 0,
      councils_expected: 0,
      projects_with_complete_evidence: 0,
    },
    projects: [],
  };
}

function normalizeCouncil(value: unknown): NationalProjectCouncil | null {
  if (typeof value === 'string' && value.trim()) {
    return { name: value.trim(), slug: null, application_count: 0 };
  }
  if (!isRecord(value)) return null;
  const name = text(value.name ?? value.council ?? value.label).trim();
  if (!name) return null;
  return {
    name,
    slug: nullableText(value.slug),
    application_count: finite(value.application_count ?? value.count),
  };
}

function normalizeProject(value: unknown, index: number): PublicNationalProject | null {
  if (!isRecord(value)) return null;
  const rawCouncils = array(value.councils ?? value.source_councils);
  const councils = rawCouncils.map(normalizeCouncil).filter((item): item is NationalProjectCouncil => Boolean(item));
  const applications = array(value.applications ?? value.linked_applications).filter(isRecord).map((item) => ({
    application_uid: text(item.application_uid ?? item.uid ?? item.id),
    reference_no: text(item.reference_no ?? item.reference, 'Reference not reported'),
    title: text(item.title ?? item.proposal, 'Planning application'),
    council: text(item.council ?? item.source_council_name ?? item.source_council, 'Council not reported'),
    address: text(item.address),
    status: text(item.status, 'Official planning record'),
    source_url: safeUrl(item.source_url ?? item.url ?? item.application_path),
    relationship: evidenceKind(item.relationship ?? item.evidence_kind ?? 'source'),
    source_hash: nullableText(item.source_hash),
  }));
  const sources = array(value.sources ?? value.evidence ?? value.evidence_claims).filter(isRecord).map((item, sourceIndex) => ({
    source_key: text(item.source_key ?? item.id, `source-${sourceIndex}`),
    label: text(item.label ?? item.title ?? item.text, 'Source'),
    publisher: text(item.publisher ?? item.council, item.kind === 'agent_synthesis' ? 'CityScope synthesis' : ''),
    url: safeUrl(item.url ?? item.source_url),
    checked_at: nullableText(item.checked_at ?? item.retrieved_at),
    evidence_kind: item.kind === 'agent_synthesis' ? 'mixed' as const : evidenceKind(item.evidence_kind ?? item.kind),
  }));
  const timeline = array(value.timeline ?? value.events ?? value.updates).filter(isRecord).map((item, eventIndex) => ({
    event_key: text(item.event_key ?? item.id, `event-${eventIndex}`),
    at: text(item.at ?? item.date ?? item.created_at),
    title: text(item.title, 'Project update'),
    detail: text(item.detail ?? item.summary ?? item.description),
    evidence_kind: evidenceKind(item.evidence_kind ?? item.kind),
    source_url: safeUrl(item.source_url ?? item.url),
  }));
  const title = text(value.title ?? value.name).trim();
  const labels = record(value.labels);
  const derivedEvidenceKind = value.evidence_kind ?? value.evidence_status ?? (
    labels.summary === 'agent_inference' && labels.applications === 'official_records' ? 'mixed' : undefined
  );
  return {
    project_key: text(value.project_key ?? value.artifact_key ?? value.id, `project-${index}`),
    slug: nullableText(value.slug),
    title: title || 'Untitled national project',
    summary: text(value.summary ?? value.description),
    status: text(value.status, 'Monitoring'),
    source_state: text(value.source_state, 'Not reported'),
    review_state: text(value.review_state, 'Not reported'),
    priority: priorityLabel(value.priority),
    category: text(value.category ?? value.project_type, 'Development'),
    confidence: nullableFinite(value.confidence),
    evidence_kind: evidenceKind(derivedEvidenceKind),
    evidence_summary: text(value.evidence_summary ?? value.rationale),
    evidence_labels: {
      summary: text(labels.summary, 'not classified'),
      claims: text(labels.claims, 'not classified'),
      applications: text(labels.applications, 'not classified'),
    },
    councils,
    council_count: finite(value.council_count, councils.length),
    application_count: finite(value.application_count, applications.length),
    evidence_count: finite(value.evidence_count ?? value.source_backed_link_count, applications.filter((item) => item.relationship === 'source').length),
    inference_count: finite(value.inference_count ?? value.inferred_link_count, applications.filter((item) => item.relationship === 'inference').length),
    first_seen_at: nullableText(value.first_seen_at ?? value.first_seen),
    updated_at: nullableText(value.updated_at ?? value.generated_at),
    tags: stringArray(value.tags),
    applications,
    sources,
    timeline,
  };
}

export function normalizeNationalProjectsSnapshot(payload: unknown): NationalProjectsSnapshot {
  const envelope = Array.isArray(payload) ? {} : record(payload);
  const projectsInput = Array.isArray(payload)
    ? payload
    : array(envelope.projects ?? envelope.items ?? envelope.results);
  if (!Array.isArray(payload) && !isRecord(payload)) {
    throw new NationalProjectsApiError('The national projects API returned an invalid payload.', 502, payload);
  }
  const normalizedProjects = projectsInput
    .map(normalizeProject)
    .filter((item): item is PublicNationalProject => Boolean(item));
  // The public endpoint is already fail-closed. Keep the same boundary in the
  // browser adapter so missing/held/stale review state is never rendered after
  // an upstream regression or during a mixed-version rollout.
  const projects = normalizedProjects.filter((project) =>
    project.review_state === 'approved' && project.source_state === 'current'
  );
  const removedByPublicGate = normalizedProjects.length !== projects.length;
  const coverage = record(envelope.coverage ?? envelope.summary);
  return {
    available: typeof envelope.available === 'boolean' ? envelope.available : true,
    generated_at: nullableText(envelope.generated_at ?? envelope.updated_at),
    unavailable_reason: nullableText(envelope.unavailable_reason),
    coverage: {
      project_count: removedByPublicGate ? projects.length : finite(coverage.project_count ?? coverage.public_project_count, projects.length),
      linked_application_count: removedByPublicGate ? projects.reduce((sum, project) => sum + project.application_count, 0) : finite(coverage.linked_application_count ?? coverage.application_count, projects.reduce((sum, project) => sum + project.application_count, 0)),
      source_backed_link_count: removedByPublicGate ? projects.reduce((sum, project) => sum + project.evidence_count, 0) : finite(coverage.source_backed_link_count ?? coverage.evidence_count ?? coverage.official_application_count, projects.reduce((sum, project) => sum + project.evidence_count, 0)),
      inferred_link_count: removedByPublicGate ? projects.reduce((sum, project) => sum + project.inference_count, 0) : finite(coverage.inferred_link_count ?? coverage.inference_count, projects.reduce((sum, project) => sum + project.inference_count, 0)),
      councils_represented: removedByPublicGate ? new Set(projects.flatMap((project) => project.councils.map((item) => item.name))).size : finite(coverage.councils_represented ?? coverage.council_count),
      councils_expected: finite(coverage.councils_expected ?? coverage.expected_council_count ?? coverage.total_councils),
      projects_with_complete_evidence: removedByPublicGate ? projects.filter((project) => project.inference_count === 0).length : finite(coverage.projects_with_complete_evidence ?? coverage.source_complete_project_count),
    },
    projects,
  };
}

export async function fetchNationalProjects(
  options: { fetcher?: typeof fetch; signal?: AbortSignal } = {},
): Promise<NationalProjectsSnapshot> {
  const fetcher = options.fetcher ?? fetch;
  let response: Response;
  try {
    response = await fetcher(getNationalProjectsUrl(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: options.signal,
    });
  } catch {
    throw new NationalProjectsApiError('The national projects service could not be reached.');
  }
  const raw = await response.text();
  let body: unknown = null;
  try { body = raw.trim() ? JSON.parse(raw) : null; } catch { /* handled below */ }
  if (!response.ok) {
    const detail = isRecord(body) && typeof body.detail === 'string' ? body.detail : null;
    throw new NationalProjectsApiError(detail ?? `The national projects service returned ${response.status}.`, response.status, body);
  }
  return normalizeNationalProjectsSnapshot(body);
}
